import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Overlay,
  Dialog,
  Header,
  Title,
  Desc,
  Actions,
  Button,
  ApiError,
  ConfirmLabel,
  ConfirmInput,
  Select,
} from "./styles";
import { useTaskStore } from "store/task";
import { storageAPI } from "api";

const ResizeVolume = ({ open, setOpen, base }) => {
  const { addTask } = useTaskStore();

  const [name, setName] = useState("");
  const [size, setSize] = useState<number | "">("");
  const [unit, setUnit] = useState("TB");
  const [apiErr, setApiErr] = useState<string | null>(null);

  const nameRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    setName(base);
    setSize("");
    setUnit("TB");
    setApiErr(null);
    const t = setTimeout(() => nameRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(t);
    };
  }, [open, base]);

  const canSubmit = useMemo(
    () => Boolean(name.trim()) && Number(size) > 0 && unit,
    [name, size, unit]
  );

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    try {
      setApiErr(null);
      const res = await storageAPI.resizeVolume(
        name.trim(),
        Number(size),
        unit
      );
      if (res?.data?.task_id) {
        addTask({
          name,
          id: res.data.task_id,
          job: "Resize Volume",
          state: "pending",
        });
      }
      setOpen(false);
    } catch (err: any) {
      setApiErr(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to resize volume."
      );
    }
  };

  if (!open) return null;

  return createPortal(
    <Overlay>
      <Dialog role="dialog">
        <Header>
          <Title id="resize-volume-title">Resize Volume</Title>
        </Header>

        <Desc>볼륨의 크기를 변경합니다.</Desc>

        {apiErr && <ApiError role="alert">{apiErr}</ApiError>}

        <form
          onSubmit={onSubmit}
          style={{ display: "grid", gap: 12, marginTop: 8 }}
        >
          {/* 볼륨명 */}
          <ConfirmLabel>Volume name</ConfirmLabel>
          <ConfirmInput
            ref={nameRef}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="ex) oasys_test01"
            inputMode="text"
          />

          {/* 사이즈 */}
          <ConfirmLabel>Size</ConfirmLabel>
          <div style={{ display: "flex", gap: 8 }}>
            <ConfirmInput
              type="number"
              value={size}
              onChange={(e) => setSize(e.target.value)}
              placeholder="ex) 5"
              min={1}
              style={{ flex: "1 1 auto" }}
            />
            <Select value={unit} onChange={(e) => setUnit(e.target.value)}>
              <option value="MB">MB</option>
              <option value="GB">GB</option>
              <option value="TB">TB</option>
            </Select>
          </div>

          <Actions>
            <Button
              type="button"
              onClick={() => setOpen(false)}
              data-variant="ghost"
            >
              Cancel
            </Button>
            <Button type="submit" data-variant="primary" disabled={!canSubmit}>
              Resize
            </Button>
          </Actions>
        </form>
      </Dialog>
    </Overlay>,
    document.body
  );
};

export default ResizeVolume;
